import * as signalR from '@microsoft/signalr';
import $api, { API_URL } from '@/http';
import { auth } from '@/auth';

/**
 * Singleton-клиент SignalR-хаба /api/v1/hubs/document. Управляет одним HubConnection
 * на всю SPA-сессию и раздаёт события DocHashChanged подписчикам по docId.
 *
 * Режимы:
 *  - connecting: идёт попытка установить соединение (retry до 5 раз).
 *  - push: соединение живо; страховочный polling крутится с большим интервалом
 *    (safetyIntervalMs, 60с) — защита от сценариев, когда хаб доступен, а источник
 *    NOTIFY молчит (триггер отключён DBA-скриптом DocumentHash_DisableTrigger.sql,
 *    не накатан, listener завис, pg_notify потерялся и т.п.).
 *  - polling: хаб недоступен → HTTP-polling с частым интервалом (pollingIntervalMs,
 *    3с по умолчанию).
 *
 * Короткое замыкание: если /documentHub отвечает 404 (DocumentHashPush:Enabled=false),
 * retry'ев не делаем — сразу уходим в polling.
 */

const MAX_START_RETRIES = 5;
const START_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
const RECONNECT_PROBE_MS = 30_000;
const DEFAULT_SAFETY_POLLING_MS = 60_000;

export type DocHashHandler = (nextHash: string) => void;
export type DocumentHubMode = 'connecting' | 'push' | 'polling';

type SubscriberEntry = {
    handlers: Map<DocHashHandler, string>;
    pollInitialTimeout?: number;
    pollInterval?: number;
};

class DocumentHubClient {
    private connection?: signalR.HubConnection;
    private connectPromise?: Promise<void>;
    private subscribers = new Map<string, SubscriberEntry>();
    // Множество docId, по которым мы уже выполнили invoke('Subscribe').
    // Делает invokeSubscribe идемпотентным: повторный вызов для того же docId —
    // no-op, даже если subscribe() и runStartLoop оба пройдут по нему.
    // Сбрасывается при reconnect и при переходе в fallback-polling.
    private subscribedGroups = new Set<string>();
    private _mode: DocumentHubMode = 'connecting';
    private stopped = false;
    private reconnectProbeTimeout?: number;

    // Параметры быстрого polling (когда хаб недоступен).
    // Мигрируются из DocumentsHashService (initialDelay/interval).
    private pollingInitialDelayMs = 2_000;
    private pollingIntervalMs = 3_000;

    // Интервал страховочного polling в push-режиме. Покрывает сценарии, в которых
    // SignalR жив, но источник событий молчит (триггер отключён/не накатан).
    private safetyIntervalMs = DEFAULT_SAFETY_POLLING_MS;

    public get mode(): DocumentHubMode {
        return this._mode;
    }

    public configurePollingDelays(initialDelayMs: number, intervalMs: number) {
        this.pollingInitialDelayMs = initialDelayMs;
        this.pollingIntervalMs = intervalMs;
    }

    public configureSafetyPolling(intervalMs: number) {
        this.safetyIntervalMs = intervalMs;
    }

    /**
     * Подписка на изменения хэша конкретного документа.
     * Handler получает новый хэш — источник (push / polling / catchup) прозрачен
     * для caller'а. Caller (DocumentsHashService) сравнивает nextHash с knownHash
     * и решает — своё это сохранение или чужое.
     *
     * Поддерживает N подписок на один docId (refcount): invoke Subscribe выстрелит
     * только при первой подписке, Unsubscribe — только при последней.
     *
     * Страховочный polling запускается ВСЕГДА (даже в push-режиме, с большим
     * интервалом), чтобы перекрыть сценарии тишины источника.
     */
    public subscribe(docId: string, knownHash: string, handler: DocHashHandler): () => void {
        this.stopped = false;

        let entry = this.subscribers.get(docId);
        const isFirstHandler = !entry;

        if (!entry) {
            entry = { handlers: new Map() };
            this.subscribers.set(docId, entry);
        }
        entry.handlers.set(handler, knownHash);

        if (isFirstHandler) {
            this.ensureConnection();
            if (this.connection?.state === signalR.HubConnectionState.Connected) {
                // Connection уже установлен — subscribe немедленно.
                // Если ещё поднимается (connecting) — runStartLoop сам подпишет
                // всех pending docId после успешного start, второй invoke не нужен.
                this.invokeSubscribe(docId);
            }
            this.startPolling(docId, entry);
        }

        return () => this.unsubscribe(docId, handler);
    }

    public acknowledge(docId: string, handler: DocHashHandler, hash: string) {
        const entry = this.subscribers.get(docId);
        if (entry?.handlers.has(handler)) entry.handlers.set(handler, hash);
    }

    private notify(entry: SubscriberEntry, hash: string) {
        // Callbacks may unsubscribe/resubscribe synchronously during reload.
        for (const [handler, knownHash] of [...entry.handlers]) {
            if (hash !== knownHash && entry.handlers.has(handler)) handler(hash);
        }
    }

    private unsubscribe(docId: string, handler: DocHashHandler) {
        const entry = this.subscribers.get(docId);
        if (!entry) return;
        entry.handlers.delete(handler);
        if (entry.handlers.size > 0) return;

        this.stopPolling(entry);
        this.subscribers.delete(docId);
        this.subscribedGroups.delete(docId);

        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            this.connection.invoke('Unsubscribe', docId).catch(() => {});
        }
    }

    private ensureConnection() {
        if (this.connection) return;

        const logLevel = import.meta.env.PROD ? signalR.LogLevel.Warning : signalR.LogLevel.Trace;

        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(logLevel)
            .withUrl(`${API_URL}/hubs/document`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .withAutomaticReconnect()
            .build();

        this.connection.on('DocHashChanged', (payload: { docId: number; hash: string }) => {
            if (!payload) return;
            const docIdStr = String(payload.docId);
            const entry = this.subscribers.get(docIdStr);
            if (!entry) return;
            this.notify(entry, payload.hash);
        });

        this.connection.onreconnected(() => {
            this._mode = 'push';
            // После реконнекта ConnectionId новый → группы на сервере сброшены.
            // Чистим локальный трекинг и пересоздаём группы явным Subscribe.
            this.subscribedGroups.clear();
            for (const docId of this.subscribers.keys()) {
                this.invokeSubscribe(docId);
            }
            // Закрываем окно пропущенных NOTIFY во время разрыва одним HTTP per docId.
            for (const [docId, entry] of this.subscribers) {
                this.catchup(docId, entry);
            }
            // Страховочный polling переключаем обратно на большой интервал.
            for (const [docId, entry] of this.subscribers) {
                this.startPolling(docId, entry);
            }
        });

        this.connection.onclose(() => {
            // Вызывается только когда withAutomaticReconnect исчерпан.
            if (!this.stopped) {
                this.switchToPolling();
            }
        });

        this.startConnection();
    }

    private startConnection() {
        if (this.connectPromise) return;
        this.connectPromise = this.runStartLoop().finally(() => {
            this.connectPromise = undefined;
        });
    }

    private async runStartLoop() {
        this._mode = 'connecting';
        for (let attempt = 0; attempt < MAX_START_RETRIES; attempt++) {
            if (this.stopped) return;
            try {
                await this.connection!.start();
                this._mode = 'push';
                // Переподписываем все pending групп (то, что добавилось в Map пока мы коннектились).
                for (const docId of this.subscribers.keys()) {
                    this.invokeSubscribe(docId);
                }
                // Перезапускаем polling с safety-интервалом — страховка от тишины NOTIFY.
                for (const [docId, entry] of this.subscribers) {
                    this.startPolling(docId, entry);
                }
                return;
            } catch (err) {
                // 404 — хаб не смаплен (DocumentHashPush:Enabled=false). Это стабильное
                // состояние до рестарта бэка: retry и reconnect probes бессмысленны.
                if (isHttp404(err)) {
                    console.warn(
                        'DocumentHub: /documentHub returns 404 (push disabled on backend), falling back to polling permanently',
                    );
                    this.switchToPolling(true);
                    return;
                }
                const delayMs = START_RETRY_DELAYS_MS[Math.min(attempt, START_RETRY_DELAYS_MS.length - 1)];
                console.warn(
                    `DocumentHub: connect attempt ${attempt + 1}/${MAX_START_RETRIES} failed, retry in ${delayMs}ms`,
                    err,
                );
                await new Promise((r) => setTimeout(r, delayMs));
            }
        }
        console.error('DocumentHub: falling back to polling');
        this.switchToPolling();
    }

    private invokeSubscribe(docId: string) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
        if (this.subscribedGroups.has(docId)) return;
        this.subscribedGroups.add(docId);
        this.connection.invoke('Subscribe', docId).catch((err) => {
            this.subscribedGroups.delete(docId);
            console.warn(`DocumentHub: Subscribe(${docId}) failed`, err);
        });
    }

    /**
     * Переход в fallback-polling режим.
     * @param permanent true — endpoint стабильно недоступен (404, DocumentHashPush:Enabled=false);
     *                  reconnect-probes не планируются. false — транзитный разрыв;
     *                  раз в RECONNECT_PROBE_MS пробуем вернуться в push.
     */
    private switchToPolling(permanent = false) {
        if (this._mode === 'polling' || this.stopped) return;
        this._mode = 'polling';
        // Connection сброшен — серверных подписок больше нет, чистим локальный трекинг.
        this.subscribedGroups.clear();
        // Перезапускаем polling с быстрым интервалом (safety → fallback).
        for (const [docId, entry] of this.subscribers) {
            this.startPolling(docId, entry);
        }
        if (!permanent) this.scheduleReconnectProbe();
    }

    private scheduleReconnectProbe() {
        window.clearTimeout(this.reconnectProbeTimeout);
        this.reconnectProbeTimeout = window.setTimeout(() => this.tryReconnectToPush(), RECONNECT_PROBE_MS);
    }

    private async tryReconnectToPush() {
        if (this.stopped || this.subscribers.size === 0 || !this.connection) return;
        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.scheduleReconnectProbe();
            return;
        }
        try {
            await this.connection.start();
            this._mode = 'push';
            this.subscribedGroups.clear();
            for (const docId of this.subscribers.keys()) this.invokeSubscribe(docId);
            // Перезапускаем polling — fast (3с) → safety (60с).
            for (const [docId, entry] of this.subscribers) this.startPolling(docId, entry);
        } catch {
            this.scheduleReconnectProbe();
        }
    }

    /**
     * Запускает polling для docId. Интервал выбирается по текущему режиму:
     *   - polling: pollingIntervalMs (быстрый, 3с) — хаб недоступен.
     *   - connecting / push: safetyIntervalMs (большой, 60с) — страховка.
     * Первый тик выполняется по истечении initialDelay (а не initialDelay+interval),
     * дальше каждые interval мс. Идемпотентный — сначала чистит предыдущие таймеры.
     */
    private startPolling(docId: string, entry: SubscriberEntry) {
        this.stopPolling(entry);
        const intervalMs = this._mode === 'polling' ? this.pollingIntervalMs : this.safetyIntervalMs;
        const initialDelayMs = this._mode === 'polling' ? this.pollingInitialDelayMs : intervalMs;
        entry.pollInitialTimeout = window.setTimeout(() => {
            this.pollTick(docId, entry);
            entry.pollInterval = window.setInterval(() => this.pollTick(docId, entry), intervalMs);
        }, initialDelayMs);
    }

    private stopPolling(entry: SubscriberEntry) {
        if (entry.pollInitialTimeout !== undefined) window.clearTimeout(entry.pollInitialTimeout);
        if (entry.pollInterval !== undefined) window.clearInterval(entry.pollInterval);
        entry.pollInitialTimeout = undefined;
        entry.pollInterval = undefined;
    }

    private async pollTick(docId: string, entry: SubscriberEntry) {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        try {
            const res = await $api.get<{ hash: string }>(`/documentHash/${docId}`);
            const freshHash = res.data?.hash;
            if (freshHash && this.subscribers.get(docId) === entry) this.notify(entry, freshHash);
        } catch (err) {
            console.error(`DocumentHub: polling error for ${docId}:`, err);
        }
    }

    private async catchup(docId: string, entry: SubscriberEntry) {
        try {
            const res = await $api.get<{ hash: string }>(`/documentHash/${docId}`);
            const freshHash = res.data?.hash;
            if (freshHash && this.subscribers.get(docId) === entry) this.notify(entry, freshHash);
        } catch (err) {
            console.error(`DocumentHub: catchup error for ${docId}:`, err);
        }
    }
}

function isHttp404(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const statusCode = (err as { statusCode?: unknown }).statusCode;
    if (statusCode === 404) return true;
    // @microsoft/signalr иногда заворачивает ошибку в Error.message с текстом.
    const message = (err as { message?: unknown }).message;
    return typeof message === 'string' && /status code[^0-9]*404/i.test(message);
}

export const documentHub = new DocumentHubClient();
